import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ShowOwnershipStatus,
  InstallationDescriptor,
  ShowPackageImportPreview,
  ShowPackageImportResult,
  HandoverPreparation,
  HandoverBundle,
  HandoverApplyResult,
  ShowPackageCandidates,
} from '@schemas'
import { baseUrl, fetchWithActorHeaders } from '@/lib/apiClient'
import { useCurrentUser } from './hooks'

export class TransferError extends Error {
  constructor(
    public code: string,
    public status: number,
    public diagnostics: { code: string; path: string; message: string }[] = [],
    // False when the response lacked this API's error code, such as a proxy's empty 499.
    public fromApi = true
  ) {
    super(code.replaceAll('_', ' '))
  }
}
// Only this API's error response proves that a transfer action was refused. A proxy
// can answer while the API is still completing the action, so treat its status like
// a lost connection and check the exact request's result before anything else.
export function isUncertainTransferFailure(error: unknown) {
  return !(error instanceof TransferError) || !error.fromApi || error.status >= 500
}
export async function transferRequest(path: string, body?: unknown): Promise<unknown> {
  const response = await fetchWithActorHeaders(`${baseUrl}/api/admin/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    cache: 'no-store',
    ...(body === undefined
      ? {}
      : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => null)
    const fromApi = typeof error?.code === 'string'
    throw new TransferError(fromApi ? error.code : 'TRANSFER_FAILED', response.status, [], fromApi)
  }
  return response.json()
}
export const transferApi = {
  preview: async (body: unknown, handover: boolean, delivery = false) =>
    ShowPackageImportPreview.parse(
      await transferRequest(
        delivery
          ? 'show-deliveries/preview'
          : handover
            ? 'show-handovers/preview'
            : 'show-package-imports/preview',
        body
      )
    ),
  apply: async (body: unknown, handover: boolean, delivery = false) => {
    const result = await transferRequest(
      delivery
        ? 'show-deliveries/apply'
        : handover
          ? 'show-handovers/apply'
          : 'show-package-imports/apply',
      body
    )
    return handover ? HandoverApplyResult.parse(result) : ShowPackageImportResult.parse(result)
  },
  candidates: async (body: unknown) =>
    ShowPackageCandidates.parse(await transferRequest('show-package-imports/candidates', body)),
  prepare: async (body: unknown) =>
    HandoverPreparation.parse(await transferRequest('show-handovers/prepare', body)),
  freeze: async (showId: string, body: unknown) =>
    HandoverBundle.parse(
      await transferRequest(`shows/${encodeURIComponent(showId)}/handovers/freeze`, body)
    ),
}
export function useOwnership(showId: string) {
  const user = useCurrentUser()
  return useQuery({
    queryKey: ['show-ownership', user.data?.id, showId],
    enabled: Boolean(user.data?.isAdmin && showId),
    queryFn: async () =>
      ShowOwnershipStatus.parse(
        await transferRequest(`shows/${encodeURIComponent(showId)}/ownership`)
      ),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: 15000,
  })
}
export function useInstallationIdentity() {
  const user = useCurrentUser()
  return useQuery({
    queryKey: ['transfer-identity', user.data?.id],
    enabled: Boolean(user.data?.isAdmin),
    queryFn: async () =>
      InstallationDescriptor.parse(await transferRequest('show-handover-identity')),
    gcTime: 0,
    retry: false,
  })
}
export function useTransferAction<TInput, TResult>(action: (input: TInput) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: action,
    retry: false,
    gcTime: 0,
    onSettled: () => {
      for (const key of ['show-ownership', 'shows', 'organizations', 'show-series'])
        void client.invalidateQueries({ queryKey: [key] })
    },
  })
}
export async function downloadExport(showId: string, privacyProfile: 'limited' | 'full-contact') {
  const response = await fetchWithActorHeaders(
    `${baseUrl}/api/admin/shows/${encodeURIComponent(showId)}/package-export`,
    {
      method: 'POST',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ privacyProfile }),
    }
  )
  if (!response.ok) {
    const error = await response.json().catch(() => null)
    const diagnostics = Array.isArray(error?.diagnostics)
      ? error.diagnostics
          .filter(
            (item: unknown): item is { code: string; path: string; message: string } =>
              !!item &&
              typeof item === 'object' &&
              'code' in item &&
              typeof item.code === 'string' &&
              'path' in item &&
              typeof item.path === 'string' &&
              'message' in item &&
              typeof item.message === 'string'
          )
          .slice(0, 100)
      : []
    throw new TransferError(
      typeof error?.code === 'string' ? error.code : 'SHOW_PACKAGE_EXPORT_FAILED',
      response.status,
      diagnostics
    )
  }
  const name = response.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1]
  downloadBlob(
    await response.blob(),
    name?.replace(/[^a-zA-Z0-9._-]/g, '_') ?? `show-${privacyProfile}.json`
  )
}
export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob),
    a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
export function downloadJson(value: unknown, name: string, compact = false) {
  downloadBlob(
    new Blob([JSON.stringify(value, null, compact ? undefined : 2)], { type: 'application/json' }),
    name
  )
}
